import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $transactionStore } from '@lib/store';
import { useListState } from '@shared/hooks';
import { DateUtils, FormatUtils, AmountUtils, ColorUtils, PaletteUtils, cn } from '@shared/utils';
import { Table, TableCellType, Text } from '@shared/widgets';

interface ITransactionsTable {
  cardId?: number;
  className?: string;
}

export const TransactionsTable = (props: ITransactionsTable) => {
  const location = useLocation();

  const transactionStore = useUnit($transactionStore.store);

  const { t } = useTranslation();
  const transactionsState = useListState({ list: 'cardsTransactions' });

  useEffect(() => {
    $transactionStore.events.update({
      FromIndex: transactionsState.fromIndex,
      CardId: props.cardId,
      Count: transactionsState.count,
      ReturnTotalCount: true,
    });
  }, [location.pathname, transactionsState]);

  const columns = useMemo(
    () => [
      t('tables.transaction.columns.status'),
      t('tables.transaction.columns.date'),
      t('tables.transaction.columns.description'),
      t('tables.transaction.columns.originalAmount'),
      t('tables.transaction.columns.amount'),
      t('tables.transaction.columns.card'),
    ],
    [t],
  );
  return (
    <div className={cn('mb-4', PaletteUtils.isVcfcPalette && 'bg-white px-7 py-6 dark:bg-soft-black', props.className)}>
      <div className='mb-6 mt-5 flex flex-wrap items-center justify-between'>
        <Text className='pr-4' weight={PaletteUtils.isVcfcPalette ? 'semibold' : 'medium'} size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
          {t('tables.transaction.title')}
        </Text>
      </div>
      <Table
        isScrollable
        isLoading={transactionStore.loading}
        columns={columns}
        items={transactionStore?.data.data.map((row) => {
          const color = ColorUtils.getTransactionStatusStyles(row.status);
          return [
            {
              type: TableCellType.Custom,
              value: (
                <div className={cn('whitespace-nowrap px-2 py-1 text-center font-normal', color.bg, color.text)}>
                  {t(`statuses.cardTransaction.${row.status}`)}
                </div>
              ),
            },
            { type: TableCellType.Text, value: DateUtils.formatDate(row.date) },
            { type: TableCellType.Text, value: row.description },
            { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(row.amount, row.currency) },
            { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(row.totalAmount, row.baseCurrency) },
            { type: TableCellType.Text, value: row.cardNumber && FormatUtils.toSliceNumber(row.cardNumber) },
          ];
        })}
        pagination={{
          totalItemCount: transactionStore.data.total,
          currentPage: transactionsState.page,
          countItemPage: transactionsState.count,
          onChangePage: transactionsState.onChangePage,
          onChangeCountItemPage: transactionsState.onChangeCount,
        }}
      />
    </div>
  );
};
